import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import authenticatedAxios from '../../authenticatedAxios';
import { baseurl } from '../../api';
import { SuccessAlert } from '../../components/common/AlertBox/GreenAlert';
import { showAlert } from '../../components/common/ExportLabel/Exporterror';
import '../../components/common/InputBox/InputBox.css';
import '../../components/common/CommonText.css';

const ApiConfigurations = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savingCard, setSavingCard] = useState(null);
  const [editingCards, setEditingCards] = useState({});

  const initialFormState = {
    GOOGLE_MAPS_API_KEY: '',
    GROQ_API_KEY: '',
    RAZOR_KEY_ID: '',
    RAZOR_KEY_SECRET: '',
    STRIPE_SECRET_KEY: '',
    STRIPE_PUBLISHABLE_KEY: '',
    STRIPE_WEBHOOK_SECRET: '',
    RECAPTCHA_SECRET_KEY: '',
    SOCIAL_AUTH_GOOGLE_OAUTH2_CLIENT_ID: '',
    SOCIAL_AUTH_GOOGLE_OAUTH2_CLIENT_SECRET: '',
  };

  const [formValues, setFormValues] = useState(initialFormState);
  const [initialValues, setInitialValues] = useState(initialFormState);

  useEffect(() => {
    fetchApiConfigurations();
  }, []);

  const fetchApiConfigurations = async () => {
    try {
      setLoading(true);
      const response = await authenticatedAxios.get(`${baseurl}super-admin/api-configurations/`);
      if (response?.data?.data) {
        const data = response.data.data;
        const loadedValues = {
          GOOGLE_MAPS_API_KEY: data.GOOGLE_MAPS_API_KEY || '',
          GROQ_API_KEY: data.GROQ_API_KEY || '',
          RAZOR_KEY_ID: data.RAZOR_KEY_ID || '',
          RAZOR_KEY_SECRET: data.RAZOR_KEY_SECRET || '',
          STRIPE_SECRET_KEY: data.STRIPE_SECRET_KEY || '',
          STRIPE_PUBLISHABLE_KEY: data.STRIPE_PUBLISHABLE_KEY || '',
          STRIPE_WEBHOOK_SECRET: data.STRIPE_WEBHOOK_SECRET || '',
          RECAPTCHA_SECRET_KEY: data.RECAPTCHA_SECRET_KEY || '',
          SOCIAL_AUTH_GOOGLE_OAUTH2_CLIENT_ID: data.SOCIAL_AUTH_GOOGLE_OAUTH2_CLIENT_ID || '',
          SOCIAL_AUTH_GOOGLE_OAUTH2_CLIENT_SECRET: data.SOCIAL_AUTH_GOOGLE_OAUTH2_CLIENT_SECRET || '',
        };
        setFormValues(loadedValues);
        setInitialValues(loadedValues);
      }
    } catch (error) {
      console.error('Error fetching API Configurations:', error);
      if (error?.response?.data?.error_code === 1001) {
        navigate('/permission-denied');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const toggleEditCard = (cardId, isEditing) => {
    setEditingCards((prev) => ({
      ...prev,
      [cardId]: isEditing,
    }));
  };

  const handleCancelCard = (cardId, cardFields) => {
    setFormValues((prev) => {
      const restored = { ...prev };
      cardFields.forEach((field) => {
        restored[field.name] = initialValues[field.name];
      });
      return restored;
    });
    toggleEditCard(cardId, false);
  };

  const handleSaveCard = async (cardId) => {
    try {
      setSavingCard(cardId);
      const response = await authenticatedAxios.post(
        `${baseurl}super-admin/api-configurations/`,
        formValues
      );
      if (response?.data) {
        const data = response.data.data;
        const updatedValues = {
          GOOGLE_MAPS_API_KEY: data.GOOGLE_MAPS_API_KEY || '',
          GROQ_API_KEY: data.GROQ_API_KEY || '',
          RAZOR_KEY_ID: data.RAZOR_KEY_ID || '',
          RAZOR_KEY_SECRET: data.RAZOR_KEY_SECRET || '',
          STRIPE_SECRET_KEY: data.STRIPE_SECRET_KEY || '',
          STRIPE_PUBLISHABLE_KEY: data.STRIPE_PUBLISHABLE_KEY || '',
          STRIPE_WEBHOOK_SECRET: data.STRIPE_WEBHOOK_SECRET || '',
          RECAPTCHA_SECRET_KEY: data.RECAPTCHA_SECRET_KEY || '',
          SOCIAL_AUTH_GOOGLE_OAUTH2_CLIENT_ID: data.SOCIAL_AUTH_GOOGLE_OAUTH2_CLIENT_ID || '',
          SOCIAL_AUTH_GOOGLE_OAUTH2_CLIENT_SECRET: data.SOCIAL_AUTH_GOOGLE_OAUTH2_CLIENT_SECRET || '',
        };
        setFormValues(updatedValues);
        setInitialValues(updatedValues);
        toggleEditCard(cardId, false);
        SuccessAlert('API Configurations updated successfully!');
      }
    } catch (error) {
      console.error('Error updating API Configurations:', error);
      showAlert(
        error?.response?.data?.message || 'Failed to update API Configurations.'
      );
    } finally {
      setSavingCard(null);
    }
  };

  const cardGroups = [
    {
      id: 'general_maps',
      title: 'API Configurations',
      fields: [
        { name: 'GOOGLE_MAPS_API_KEY', label: 'Google Map Key' },
        { name: 'GROQ_API_KEY', label: 'Groq API Key' },
      ],
    },
    {
      id: 'razorpay',
      title: 'Razorpay Configurations',
      fields: [
        { name: 'RAZOR_KEY_ID', label: 'Razor Key Id' },
        { name: 'RAZOR_KEY_SECRET', label: 'Razor Key Secret' },
      ],
    },
    {
      id: 'stripe',
      title: 'Stripe Configurations',
      fields: [
        { name: 'STRIPE_SECRET_KEY', label: 'Stripe Secret Key' },
        { name: 'STRIPE_PUBLISHABLE_KEY', label: 'Stripe Publishable Key' },
        { name: 'STRIPE_WEBHOOK_SECRET', label: 'Stripe Webhook Secret' },
      ],
    },
    {
      id: 'social_recaptcha',
      title: 'Google & Recaptcha Configurations',
      fields: [
        { name: 'RECAPTCHA_SECRET_KEY', label: 'Recaptcha Secret Key' },
        { name: 'SOCIAL_AUTH_GOOGLE_OAUTH2_CLIENT_ID', label: 'Social Auth Google OAuth2 Client ID' },
        { name: 'SOCIAL_AUTH_GOOGLE_OAUTH2_CLIENT_SECRET', label: 'Social Auth Google OAuth2 Client Secret' },
      ],
    },
  ];

  if (loading) {
    return (
      <div className="whole-container">
        <PageHeader pageName="Home / Settings /" nestedpageName="API Configurations" linkTo="/internal-users" />
        <div style={{ padding: '20px', textAlign: 'center', fontSize: '0.875rem' }}>Loading API Configurations...</div>
      </div>
    );
  }

  return (
    <div className="whole-container">
      <div className="top-heading">
        <PageHeader pageName="Home / Settings /" nestedpageName="API Configurations" linkTo="/internal-users" />
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          marginTop: '20px',
          marginInlineStart: '1.5rem',
          marginInlineEnd: '1.5rem',
          maxWidth: '600px',
        }}
      >
        {cardGroups.map((group) => {
          const isCardEditing = Boolean(editingCards[group.id]);
          const isSavingThisCard = savingCard === group.id;

          return (
            <div
              key={group.id}
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '8px',
                padding: '24px',
                width: '100%',
                boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.05)',
                border: '1px solid #EAEAEA',
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                }}
              >
                <h3
                  className="common-text ftw-5"
                  style={{ fontSize: '1rem', color: '#1A1A1A', margin: 0 }}
                >
                  {group.title}
                </h3>

                <div style={{ display: 'flex', gap: '8px' }}>
                  {!isCardEditing ? (
                    <button
                      type="button"
                      onClick={() => toggleEditCard(group.id, true)}
                      style={{
                        backgroundColor: '#FFFFFF',
                        color: '#094C80',
                        border: '1px solid #094C80',
                        borderRadius: '8px',
                        padding: '6px 20px',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      Edit
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => handleSaveCard(group.id)}
                        disabled={isSavingThisCard}
                        style={{
                          backgroundColor: '#094C80',
                          color: '#FFFFFF',
                          border: '1px solid #094C80',
                          borderRadius: '8px',
                          padding: '6px 20px',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          cursor: isSavingThisCard ? 'not-allowed' : 'pointer',
                          opacity: isSavingThisCard ? 0.7 : 1,
                        }}
                      >
                        {isSavingThisCard ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCancelCard(group.id, group.fields)}
                        disabled={isSavingThisCard}
                        style={{
                          backgroundColor: '#FFFFFF',
                          color: '#666666',
                          border: '1px solid #CCCCCC',
                          borderRadius: '8px',
                          padding: '6px 16px',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                        }}
                      >
                        Close
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {group.fields.map((field) => {
                  const val = formValues[field.name];
                  const hasVal = Boolean(val && val.length > 0);

                  return (
                    <div key={field.name} className="custom-field-container" style={{ width: '100%' }}>
                      <input
                        type="text"
                        name={field.name}
                        id={field.name}
                        value={val}
                        onChange={handleInputChange}
                        disabled={!isCardEditing}
                        className="input-box"
                        style={{
                          backgroundColor: isCardEditing ? '#FFFFFF' : '#FAFAFA',
                          color: isCardEditing ? '#333333' : '#666666',
                          cursor: isCardEditing ? 'text' : 'default',
                          fontSize: '0.75rem',
                        }}
                      />
                      <label
                        htmlFor={field.name}
                        className={`input-label ${hasVal || isCardEditing ? 'has-value' : ''}`}
                        style={{ fontSize: (hasVal || isCardEditing) ? '0.65rem' : '0.75rem' }}
                      >
                        {field.label}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ApiConfigurations;
